/**
 * The shared component kit. One implementation, used by both shells.
 * Nothing here knows whether it is on a phone or a desktop — density does that.
 */
import type { ReactNode } from 'react'
import { money, kg as fmtKg, pct, signedPct, toNumber } from '../lib/format'
import { Photo } from './PhotoButton'

type Tone = 'ok' | 'warn' | 'crit' | 'info' | 'neutral'

// ---------------------------------------------------------------- panel
export function Panel({
  title, meta, children, footer, className = '',
}: {
  title?: ReactNode
  meta?: ReactNode
  children: ReactNode
  footer?: ReactNode
  className?: string
}) {
  return (
    <section className={`panel ${className}`}>
      {(title || meta) && (
        <header className="panel-head">
          {typeof title === 'string' ? <h3>{title}</h3> : title}
          {meta && <div className="meta">{meta}</div>}
        </header>
      )}
      {children}
      {footer && <div className="panel-foot">{footer}</div>}
    </section>
  )
}

// ---------------------------------------------------------------- kpi
export function Kpi({
  label, value, unit, sub, meterPct, tone = 'neutral', hint,
}: {
  label: string
  value: ReactNode
  unit?: string
  sub?: ReactNode
  meterPct?: number | null
  tone?: Tone
  hint?: ReactNode
}) {
  const cls = tone === 'crit' ? 'is-crit' : tone === 'warn' ? 'is-warn' : ''
  const meterCls = tone === 'crit' ? 'is-crit' : tone === 'warn' ? 'is-warn' : ''
  return (
    <div className={`kpi ${cls}`}>
      <div className="kpi-label">
        <span className="micro">{label}</span>
        {hint}
      </div>
      <div className="kpi-value num">
        {value}
        {unit && <small> {unit}</small>}
      </div>
      {sub && <div className="kpi-sub">{sub}</div>}
      {meterPct !== undefined && meterPct !== null && (
        <div className={`meter ${meterCls}`}>
          <i style={{ width: `${Math.max(0, Math.min(100, meterPct))}%` }} />
        </div>
      )}
    </div>
  )
}

export function StatMini({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  return (
    <div className="mini">
      <i>{label}</i>
      <b className="num">{value}</b>
      {note && <span>{note}</span>}
    </div>
  )
}

// ---------------------------------------------------------------- pill / delta
export function Pill({ tone = 'neutral', dot = true, children }: { tone?: Tone; dot?: boolean; children: ReactNode }) {
  const cls = tone === 'neutral' ? '' : tone
  return <span className={`pill ${cls} ${dot ? '' : 'no-dot'}`}>{children}</span>
}

/**
 * A signed percentage. `goodDirection` says which way is good — a shrink of -3%
 * is bad, a fill rate of +3% is good, and the colour must follow the meaning.
 */
export function Delta({ value, goodDirection = 'up', decimals = 1 }: {
  value: number | string | null | undefined
  goodDirection?: 'up' | 'down' | 'zero'
  decimals?: number
}) {
  const n = toNumber(value)
  if (n === null) return <span className="delta flat">—</span>
  let cls: string
  if (goodDirection === 'zero') cls = Math.abs(n) < 0.01 ? 'flat' : 'down'
  else if (n === 0) cls = 'flat'
  else if (goodDirection === 'up') cls = n > 0 ? 'up' : 'down'
  else cls = n < 0 ? 'up' : 'down'
  return <span className={`delta ${cls}`}>{signedPct(n, decimals)}</span>
}

export function Money({ value, compact, currency }: {
  value: number | string | null | undefined
  compact?: boolean
  currency?: boolean
}) {
  return <span className="num">{money(value, { compact, currency })}</span>
}

export function Weight({ value, decimals = 1 }: { value: number | string | null | undefined; decimals?: number }) {
  return <span className="num">{fmtKg(value, decimals)}</span>
}

export function Pct({ value, decimals = 1 }: { value: number | string | null | undefined; decimals?: number }) {
  return <span className="num">{pct(value, decimals)}</span>
}

// ---------------------------------------------------------------- alerts
export function AlertCard({
  tone = 'info', tag, title, children, actions,
}: {
  tone?: 'crit' | 'warn' | 'info'
  tag: string
  title: ReactNode
  children?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className={`alert ${tone}`}>
      <div className="alert-icon">
        {tone === 'crit' ? <IconAlert /> : tone === 'warn' ? <IconClock /> : <IconInfo />}
      </div>
      <div className="grow">
        <span className="alert-tag">{tag}</span>
        <h4>{title}</h4>
        {children && <p>{children}</p>}
        {actions && <div className="alert-cta">{actions}</div>}
      </div>
    </div>
  )
}

export function Banner({
  tone = 'warn', icon, title, children,
}: {
  tone?: 'crit' | 'warn' | 'info'
  icon?: ReactNode
  title: ReactNode
  children?: ReactNode
}) {
  return (
    <div className={`banner ${tone}`}>
      <div className="banner-icon">{icon ?? <IconAlert />}</div>
      <div className="grow">
        <b>{title}</b>
        {children && <p>{children}</p>}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- evidence
/**
 * What can actually be shown to somebody in an argument.
 *
 * These chips used to say "photo" whenever a column was non-empty, which was true of
 * the string 'photo://farmgate' - a value that was never a photograph. And "GPS"
 * whenever a latitude existed, however rough the fix. Both are now the honest
 * question: is there a real picture, and is the position good enough to stand behind.
 */
export function Evidence({ photo, gps, accuracyM, distanceM, tooFar, temp, scale }: {
  photo?: boolean
  gps?: boolean
  /** How good the fix was, in metres. */
  accuracyM?: number | string | null
  /** How far from where this should have happened. */
  distanceM?: number | string | null
  tooFar?: boolean
  temp?: number | string | null
  scale?: string | null
}) {
  const t = toNumber(temp)
  const acc = toNumber(accuracyM)
  const dist = toNumber(distanceM)

  return (
    <div className="evidence">
      {photo !== undefined && (
        <span className={`chip ${photo ? 'on' : 'off'}`}>{photo ? '▣ photo' : '▣ no photo'}</span>
      )}

      {gps !== undefined && (
        <span className={`chip ${gps ? 'on' : 'off'}`}>
          {gps
            ? `◎ ${acc === null ? 'GPS' : `±${Math.round(acc)} m`}`
            : acc === null ? '◎ no GPS' : `◎ GPS ±${Math.round(acc)} m — too rough`}
        </span>
      )}

      {dist !== null && (
        <span className={`chip ${tooFar ? 'off' : 'on'}`} title="Distance from where this should have happened">
          {dist < 1000 ? `⌖ ${Math.round(dist)} m away` : `⌖ ${(dist / 1000).toFixed(1)} km away`}
        </span>
      )}

      {t !== null && <span className="chip on">{t.toFixed(1)}°C</span>}
      {scale && <span className="chip">⚖ {scale}</span>}
    </div>
  )
}

// ---------------------------------------------------------------- trace chain
export interface ChainNode {
  point: string
  netKg: number | string
  gpsAccuracyM?: number | null
  distanceM?: number | null
  farFromWhereItShouldBe?: boolean
  photoUrl?: string | null
  weighedBy?: { name: string } | null
  weighedAt?: string
  scale?: { code?: string | null } | null
  hasPhoto?: boolean
  hasGps?: boolean
  temperatureC?: number | string | null
  deltaPct?: number | null
  tone?: 'ok' | 'warn' | 'crit'
  attributions?: Array<{ label: string; deltaPct: number | string }>
}

const POINT_LABEL: Record<string, string> = {
  farmgate: 'Weighed at the farm · bought',
  depot_in: 'Weighed at the store · graded',
  dispatch: 'Loaded onto the van',
  delivered: 'Signed for by the buyer · invoiced',
  return: 'Came back to the store',
}

export function TraceChain({ nodes }: { nodes: ChainNode[] }) {
  return (
    <div className="chain">
      {nodes.map((n, i) => (
        <div className="chain-step" key={`${n.point}-${i}`}>
          <div className={`chain-dot ${n.tone ?? 'ok'}`}>{i + 1}</div>
          <div className="chain-body">
            <div className="chain-top">
              <span className="chain-kg">
                {Number(n.netKg).toFixed(1)} <small>kg</small>
              </span>
              {n.deltaPct !== undefined && n.deltaPct !== null && i > 0 && (
                <Delta value={n.deltaPct} goodDirection="zero" />
              )}
            </div>
            <div className="micro" style={{ marginTop: 2 }}>{POINT_LABEL[n.point] ?? n.point}</div>
            <div className="chain-meta">
              {n.weighedAt && <>{new Date(n.weighedAt).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', hour12: false })} · </>}
              {n.weighedBy?.name && <b>{n.weighedBy.name}</b>}
              {n.scale?.code && <> · scale {n.scale.code}</>}
            </div>
            <div style={{ marginTop: 8 }}>
              <Evidence
                photo={n.hasPhoto}
                gps={n.hasGps}
                accuracyM={n.gpsAccuracyM}
                distanceM={n.distanceM}
                tooFar={n.farFromWhereItShouldBe}
                temp={n.temperatureC}
              />
              {n.photoUrl?.startsWith('media:') && (
                <div style={{ marginTop: 8 }}>
                  <Photo refValue={n.photoUrl} alt={`${n.point} photo`} />
                </div>
              )}
            </div>
            {n.attributions && n.attributions.length > 0 && (
              <div className="evidence" style={{ marginTop: 8 }}>
                {n.attributions.map((a) => (
                  <span className="chip" key={a.label}>
                    {a.label} {signedPct(a.deltaPct)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------- misc
export function Stepper({ total, current }: { total: number; current: number }) {
  return (
    <div className="stepper">
      {Array.from({ length: total }, (_, i) => (
        <i key={i} className={i < current - 1 ? 'done' : i === current - 1 ? 'cur' : ''} />
      ))}
    </div>
  )
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <b>{title}</b>
      {children && <p>{children}</p>}
      {action}
    </div>
  )
}

export function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div className="panel-body col" style={{ gap: 10 }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 18, width: `${100 - i * 9}%` }} />
      ))}
    </div>
  )
}

export function ErrorNote({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : 'Something went wrong.'
  return (
    <div className="panel-body">
      <Banner tone="crit" title="Could not load this">
        {msg}
      </Banner>
      {onRetry && (
        <button className="btn ghost" style={{ marginTop: 12 }} onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- icons
// Small, consistent, stroke-based. Sized by the caller's font where possible.
const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }

export const IconLeaf = ({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.2} strokeLinecap="round">
    <path d="M12 22V9M12 9c0-4 3-6 8-6 0 5-2.5 8-8 8z" />
    <path d="M12 13c0-3-2-5-6-5 0 4 1.8 6.4 6 6.6z" opacity=".75" />
  </svg>
)
export const IconAlert = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9.5" /><path d="M12 7.5v5.5M12 16.5v.5" /></svg>
)
export const IconClock = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9.5" /><path d="M12 7v5l3 2" /></svg>
)
export const IconInfo = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9.5" /><path d="M12 11v5.5M12 7.5v.5" /></svg>
)
export const IconScale = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><path d="M12 3v18M7 21h10M4 8h16M4 8l-2.5 6a3.2 3.2 0 005 0zM20 8l2.5 6a3.2 3.2 0 01-5 0z" /></svg>
)
export const IconGrid = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>
)
export const IconRoute = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><circle cx="6" cy="5" r="2.5" /><circle cx="18" cy="19" r="2.5" /><path d="M6 7.5v4a4 4 0 004 4h4a4 4 0 014 4v-2" /></svg>
)
export const IconUsers = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><circle cx="9" cy="8" r="3.2" /><path d="M3 20a6 6 0 0112 0" /><path d="M16 5.5a3.2 3.2 0 010 5.4M18 20a6 6 0 00-2-4.5" /></svg>
)
export const IconTag = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><path d="M3 11V4h7l10 10-7 7z" /><circle cx="7.5" cy="7.5" r="1.3" /></svg>
)
export const IconStore = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><path d="M4 9h16v11H4zM3 9l1.5-5h15L21 9M9 20v-6h6v6" /></svg>
)
export const IconTruck = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><path d="M2 6h12v10H2zM14 9h4l3 3.5V16h-7z" /><circle cx="6" cy="18.5" r="1.8" /><circle cx="17.5" cy="18.5" r="1.8" /></svg>
)
export const IconCash = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><rect x="2" y="5.5" width="20" height="13" rx="2.5" /><circle cx="12" cy="12" r="2.8" /><path d="M5.5 9v6M18.5 9v6" /></svg>
)
export const IconBox = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><path d="M12 3l8 4v10l-8 4-8-4V7z" /><path d="M4 7l8 4 8-4M12 11v10" /></svg>
)
export const IconChart = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><path d="M3 20h18M6 20v-6M11 20V7M16 20v-9M21 20V4" /></svg>
)
export const IconCog = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="3" /><path d="M19.4 13a7.7 7.7 0 000-2l2-1.5-2-3.4-2.3 1a7.6 7.6 0 00-1.8-1L15 2h-4l-.3 2.6a7.6 7.6 0 00-1.8 1l-2.3-1-2 3.4L6.6 11a7.7 7.7 0 000 2l-2 1.5 2 3.4 2.3-1a7.6 7.6 0 001.8 1L11 22h4l.3-2.6a7.6 7.6 0 001.8-1l2.3 1 2-3.4z" /></svg>
)
export const IconWifiOff = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><path d="M2 3l20 20M5 12.5a11 11 0 015-2.4M2 8.5A16 16 0 0110 6M14 6a16 16 0 018 2.5M19 12.5a11 11 0 00-2.5-1.6M8.8 16.2a5 5 0 016.4 0M12 20.5v.01" /></svg>
)
export const IconCheck = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><path d="M4 12.5l5 5L20 6.5" /></svg>
)
export const IconChevron = ({ size = 16, dir = 'right' }: { size?: number; dir?: 'left' | 'right' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}
    style={{ transform: dir === 'left' ? 'rotate(180deg)' : undefined }}>
    <path d="M9 5l7 7-7 7" />
  </svg>
)
export const IconHome = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><path d="M3 10.5L12 3l9 7.5V20a1 1 0 01-1 1h-5v-6H9v6H4a1 1 0 01-1-1z" /></svg>
)
export const IconQueue = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><path d="M4 7h16M4 12h16M4 17h10" /></svg>
)
export const IconExit = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}>
    <path d="M15 4h3a2 2 0 012 2v12a2 2 0 01-2 2h-3" />
    <path d="M10 8l-4 4 4 4M6 12h11" />
  </svg>
)
export const IconSearch = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...s}><circle cx="11" cy="11" r="7" /><path d="M16.5 16.5L21 21" /></svg>
)
